import { Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type {
  Invoice,
  InvoiceTransaction,
} from '../../domain/entities/sales.entity';
import { CopyButton } from '@/shared/components/copy-button';
import { EntityLink } from '@/shared/components/entity-link';
import { useInvoiceTransactions } from '../hooks/use-contract-details';
import {
  Field,
  InvoiceStatusBadge,
  Mono,
  TransactionStatusBadge,
  formatCurrency,
  formatDate,
  formatDateTime,
  invoiceOriginLabel,
} from './display';

/**
 * Query param que leva uma fatura específica em foco na página da Billing
 * Agreement (`/billing-agreements/:id?invoice=<invoiceId>`). Existe porque
 * fatura não tem rota própria — quem aponta para uma fatura (hoje a Central de
 * Pendências, via `alerts-pending-source.ts`) aponta para a BA dela e diz qual
 * é.
 */
export const FOCUSED_INVOICE_PARAM = 'invoice';

/**
 * Tabela de faturas e resumo de uma fatura — compartilhados pela aba "Detalhes"
 * do contrato (faturas ativas de todas as BAs) e pela página
 * `/billing-agreements/:id` (faturas de uma BA, sem filtro de status). Só a
 * origem dos dados muda entre as duas; a apresentação é a mesma.
 */

function InfoIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
      />
    </svg>
  );
}

/** Status em que o backend aceita baixa manual (`Invoice.assertCanSettleManually`). */
function canSettleManually(invoice: Invoice): boolean {
  return invoice.status === 'pending' || invoice.status === 'overdue';
}

/**
 * Valor da fatura. Ciclo futuro de assinatura de ciclos fixos é materializado
 * como placeholder (`value: null`) e só recebe valor quando o webhook daquele
 * ciclo chega — mostrar o preço vigente marcado com `~` diz mais que um traço,
 * desde que a estimativa fique visualmente distinta do valor real.
 */
function InvoiceValueCell({
  invoice,
  fallback,
}: {
  invoice: Invoice;
  fallback?: number | null;
}) {
  if (invoice.value !== null) {
    return (
      <span className="text-(--color-text-primary) tabular-nums">
        {formatCurrency(invoice.value)}
      </span>
    );
  }
  if (fallback === null || fallback === undefined) {
    return <span className="text-(--color-text-secondary)">---</span>;
  }
  return (
    <span
      className="text-(--color-text-secondary) whitespace-nowrap italic tabular-nums"
      title="Estimativa: este ciclo ainda não foi cobrado, o valor é o do ciclo mais recente conhecido."
    >
      ~ {formatCurrency(fallback)}
    </span>
  );
}

/**
 * O tooltip abre pelo **nome do caso** e só depois explica: "Cobrança real" e
 * "Estimativa" são a informação que falta para ler a célula. Sem isso, duas
 * datas com a mesma cara na mesma coluna afirmavam coisas diferentes sem dizer
 * qual era qual — e a diferença entre "foi cobrado neste dia" e "deve ser
 * cobrado por volta deste dia" é justamente a que o operador precisa.
 */
const CHARGE_DATE_TITLE: Record<Invoice['chargeDateKind'], string> = {
  charged: 'Cobrança real — foi o dia em que o gateway cobrou este ciclo.',
  estimated:
    'Estimativa — este ciclo ainda não foi cobrado. Data projetada pela periodicidade da assinatura, a partir da cobrança conhecida mais próxima.',
  closed:
    'Ciclo encerrado — cancelado ou substituído nesta data, sem ter sido cobrado.',
  unknown:
    'Sem data — este ciclo não foi cobrado e a assinatura ainda não tem periodicidade conhecida para projetar.',
};

/**
 * Quando o ciclo foi — ou será — cobrado. Substituiu "Última atualização", que
 * em todo ciclo futuro mostrava o mesmo timestamp (o instante em que a BA foi
 * materializada em lote) e não respondia a única pergunta que se faz aqui.
 *
 * O `chargeDateKind` vem do back porque o status da fatura não basta para
 * decidir a frase: `PENDING` com boleto emitido tem cobrança de verdade, e ciclo
 * cancelado depois de pago tem a data do pagamento, não a do cancelamento.
 *
 * Três aparências, porque são três afirmações diferentes: cobrança real em
 * texto primário (é fato), estimativa em `~` + itálico apagado e só o dia (a
 * projeção herda a hora da cobrança âncora — exibir minuto fingiria uma precisão
 * que ninguém tem), ciclo encerrado riscado (a data existiu, a cobrança não).
 */
export function InvoiceChargeDateCell({ invoice }: { invoice: Invoice }) {
  if (!invoice.chargeDate) {
    return (
      <span
        className="text-(--color-text-secondary)"
        title={CHARGE_DATE_TITLE.unknown}
      >
        ---
      </span>
    );
  }

  if (invoice.chargeDateKind === 'estimated') {
    return (
      <span
        className="text-(--color-text-secondary) whitespace-nowrap italic"
        title={CHARGE_DATE_TITLE.estimated}
      >
        ~ {formatDate(invoice.chargeDate)}
      </span>
    );
  }

  return (
    <span
      className={`whitespace-nowrap ${
        invoice.chargeDateKind === 'closed'
          ? 'text-(--color-text-secondary) line-through'
          : 'text-(--color-text-primary)'
      }`}
      title={CHARGE_DATE_TITLE[invoice.chargeDateKind]}
    >
      {formatDateTime(invoice.chargeDate)}
    </span>
  );
}

/**
 * @param onManualSettlement quando informado, faturas ainda em aberto ganham o
 * atalho de baixa manual. Quem chama decide se oferece (papel de escrita) e é
 * dono do modal — a ação recarrega a entidade da página, não a fatura isolada.
 * @param perCycleValue preço vigente do ciclo, para exibir o valor **estimado**
 * (prefixado por `~`) das faturas placeholder, que nascem sem valor. Só faz
 * sentido no escopo de uma BA só: num contrato renegociado, cada BA tem preço
 * próprio, então quem lista o contrato inteiro omite e essas linhas ficam com
 * traço.
 *
 * Os ids (fatura, gateway, BA, contrato) saíram das colunas de propósito: são
 * ruído numa tabela de parcelas e continuam todos no modal de resumo, a um
 * clique no ícone de info.
 */
export function buildInvoiceColumns(
  onShowDetails: (invoice: Invoice) => void,
  onManualSettlement?: (invoice: Invoice) => void,
  perCycleValue?: number | null,
): ColumnDef<Invoice>[] {
  return [
    {
      key: 'cycleIndex',
      header: 'Ciclo',
      width: '80px',
      render: (row) => (
        <span className="text-(--color-text-primary) font-medium tabular-nums">
          {row.cycleIndex}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Valor',
      width: '140px',
      render: (row) => (
        <InvoiceValueCell invoice={row} fallback={perCycleValue} />
      ),
    },
    {
      key: 'origin',
      header: 'Fonte',
      render: (row) => (
        <span className="text-(--color-text-secondary) whitespace-nowrap">
          {invoiceOriginLabel(row.origin)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '140px',
      render: (row) => <InvoiceStatusBadge status={row.status} />,
    },
    {
      key: 'chargeDate',
      header: 'Datas',
      render: (row) => <InvoiceChargeDateCell invoice={row} />,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          {onManualSettlement && canSettleManually(row) && (
            <button
              type="button"
              onClick={() => onManualSettlement(row)}
              className="border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg-secondary) whitespace-nowrap rounded-md border px-2 py-1 text-xs transition"
            >
              Dar baixa manual
            </button>
          )}
          <button
            type="button"
            onClick={() => onShowDetails(row)}
            title="Ver resumo da fatura"
            aria-label="Ver resumo da fatura"
            className="text-(--color-text-secondary) hover:text-(--color-accent) transition-colors"
          >
            <InfoIcon />
          </button>
        </div>
      ),
    },
  ];
}

/**
 * Corpo do modal "Resumo da Fatura": os campos da fatura + as tentativas de
 * cobrança dela. Antes só tinha os campos, e a tela do contrato não tinha
 * caminho nenhum até as transações — só o inverso (transação → faturas) existia.
 */
export function InvoiceSummary({ invoice }: { invoice: Invoice }) {
  return (
    <div className="space-y-6">
      <InvoiceSummaryFields invoice={invoice} />
      <InvoiceTransactionsSection invoiceId={invoice.id} />
    </div>
  );
}

/**
 * Transações da fatura, da tentativa mais recente para a mais antiga. Uma fatura
 * tem N tentativas (boleto expirado + nova tentativa, estorno, chargeback), daí
 * ser lista e não campo. O id leva à página da transação.
 */
function InvoiceTransactionsSection({ invoiceId }: { invoiceId: string }) {
  const { data, isLoading, isError } = useInvoiceTransactions(invoiceId);

  const columns: ColumnDef<InvoiceTransaction>[] = [
    {
      key: 'id',
      header: 'ID',
      render: (row) => (
        <Mono>
          <EntityLink kind="transaction" id={row.id} />
        </Mono>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <TransactionStatusBadge status={row.status} />,
    },
    {
      key: 'amount',
      header: 'Valor',
      render: (row) => <span>{formatCurrency(row.amount)}</span>,
    },
    /**
     * `occurredAt`, não `updatedAt`: o `updatedAt` é quando gravamos a linha, e
     * numa transação vinda da sincronização de histórico (`GATEWAY_BACKFILL`) é
     * o dia da sincronização. O `occurredAt` é a última movimentação da
     * tentativa no gateway, e não o início dela, porque o `applyGatewayStatus`
     * o avança a cada evento (boleto pendente → expirado, estorno). É também a
     * data pela qual a lista já vem ordenada.
     */
    {
      key: 'occurredAt',
      header: 'Data',
      render: (row) => (
        <span className="text-(--color-text-secondary) whitespace-nowrap">
          {formatDateTime(row.occurredAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-(--color-text-primary) text-sm font-semibold">
        Transações desta fatura{data && !isError ? ` (${data.length})` : ''}
      </h3>
      {isError ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Não foi possível carregar as transações desta fatura.
        </p>
      ) : (
        <Table
          columns={columns}
          data={data ?? []}
          keyExtractor={(row) => row.id}
          loading={isLoading}
          emptyMessage="Nenhuma transação nesta fatura."
          striped
        />
      )}
    </div>
  );
}

function InvoiceSummaryFields({ invoice }: { invoice: Invoice }) {
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      <Field
        label="ID externo (gateway)"
        value={invoice.externalId ? <Mono>{invoice.externalId}</Mono> : null}
      />
      <Field label="ID" value={<Mono>{invoice.id}</Mono>} />
      <Field
        label="Contrato ID"
        value={
          <Mono>
            {/* Idem `TransactionDetailPage`: só é contrato quando a fonte da
                fatura é `contract`. */}
            {invoice.sourceType === 'contract' ? (
              <EntityLink kind="contract" id={invoice.sourceId} />
            ) : (
              invoice.sourceId
            )}
          </Mono>
        }
      />
      <Field label="Tipo de fonte" value={invoice.sourceType} />
      <Field
        label="Billing Agreement ID"
        value={
          <Mono>
            <EntityLink
              kind="billingAgreement"
              id={invoice.billingAgreementId}
            />
          </Mono>
        }
      />
      <Field label="Valor" value={formatCurrency(invoice.value)} />
      <Field label="Fonte" value={invoiceOriginLabel(invoice.origin)} />
      <Field
        label="Status"
        value={<InvoiceStatusBadge status={invoice.status} />}
      />
      <Field label="Ciclo" value={invoice.cycleIndex} />
      <Field
        label="Cobrança"
        value={
          invoice.chargeDate ? (
            <InvoiceChargeDateCell invoice={invoice} />
          ) : null
        }
      />
      <Field
        label="Link de pagamento"
        value={
          invoice.paymentUrl ? (
            <PaymentUrlValue paymentUrl={invoice.paymentUrl} />
          ) : null
        }
      />
      <Field label="Criada em" value={formatDateTime(invoice.createdAt)} />
      <Field label="Atualizada em" value={formatDateTime(invoice.updatedAt)} />
    </div>
  );
}

/**
 * O suporte copia o link e manda ao aluno. Não abre daqui: a página do gateway
 * é a do aluno, com os dados dele.
 */
function PaymentUrlValue({ paymentUrl }: { paymentUrl: string }) {
  return (
    <div className="flex items-center gap-2">
      <Mono>
        <span className="break-all">{paymentUrl}</span>
      </Mono>
      <CopyButton text={paymentUrl} label="Copiar" />
    </div>
  );
}
